import CompanyPreviewPanelContent from "@/components/company/company-preview-panel-content";
import CompanyTagPreview from "@/components/company/company-tag-preview";
import {
  companyPreviewPanelStore,
  useCompanyPreviewPanelEntry,
} from "@/stores/company-preview-panel-store";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { MdClose } from "react-icons/md";
import { useEffect } from "react";

/**
 * Company preview panel rendered inline — filling its host container rather than
 * a viewport-anchored sheet. Mount it inside a positioned parent (the docked
 * company-page chat) so a clicked mention opens in the exact bounds of that
 * container instead of a differently sized sheet floating over it.
 *
 * Reads the same {@link companyPreviewPanelStore} as the global sheet, so
 * navigation and selection stay in sync; it only renders when the store's
 * surface is "inline" (the global sheet stands down for that surface).
 */
export function CompanyPreviewPanelInline() {
  const { t } = useTranslation();
  const { state, company, tag, isVisible } = useCompanyPreviewPanelEntry();

  // Leaving the chat unmounts this host, so drop any inline panel it was
  // showing — otherwise its "inline" surface would suppress the global sheet on
  // the next page (which has no inline host to render it).
  useEffect(
    () => () => {
      if (companyPreviewPanelStore.getSnapshot()?.surface === "inline") {
        companyPreviewPanelStore.close();
      }
    },
    [],
  );

  const isOpen = state?.surface === "inline" && isVisible;

  if (!isOpen) return null;

  return (
    <div className="flex absolute inset-0 z-30 flex-col border-l bg-background border-border animate-in slide-in-from-right duration-300">
      <div className="flex z-10 justify-end items-center px-2 py-1.5 border-b bg-background border-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => companyPreviewPanelStore.close()}
          aria-label={t("common.close")}
        >
          <MdClose className="size-3.5" />
        </Button>
      </div>
      <div className="flex-1 min-h-0">
        {company ? (
          <CompanyPreviewPanelContent company={company} />
        ) : (
          tag && <CompanyTagPreview tag={tag} />
        )}
      </div>
    </div>
  );
}
